import { useMemo } from "react";
import classNames from "classnames";
import { Flex, Text, HelpIndicator, AgGrid, toast, LoadingIndicator } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import { useCreateComplianceCaseFromProfileMutation } from "~/graphql/supergraphHooks";
import { useComplianceCiC } from "../../flags";
import { ProfileRowItem } from "../types";
import { container, iconContainer, iconContainerVisible, icon } from "./styles_vanilla.css";

type ProfileNameRendererProps = AgGrid.ICellRendererParams<ProfileRowItem>;

export const ProfileNameRenderer = ({ value, data, api }: ProfileNameRendererProps) => {
  const profileName = value as string;
  const enableCiC = useComplianceCiC();

  const activeCicCase = useMemo(() => {
    const activeCases = data?.activeCases ?? [];
    return activeCases.find(
      (caseInfo) => caseInfo.caseType === ComplianceCaseTypeEnum.ChangeInCircumstance,
    );
  }, [data?.activeCases]);

  const [createComplianceCaseFromProfile, { loading }] =
    useCreateComplianceCaseFromProfileMutation({
      fetchPolicy: "network-only",
      onError: (e) => toast.error(e.message),
      onCompleted: () => {
        api?.refreshServerSide({ purge: true });
        toast.success("Case created successfully");
      },
    });

  const handleIconClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (!activeCicCase) {
      if (data?.profileGuid) {
        void createComplianceCaseFromProfile({
          variables: {
            caseInput: {
              profileGuid: data.profileGuid,
              caseType: ComplianceCaseTypeEnum.ChangeInCircumstance,
              assignSelf: false,
            },
            emailInviteInput: null,
          },
        });
      }
    } else {
      window.open(`/canopy/compliance/case/${activeCicCase.id}`, "_blank");
    }
  };

  if (!profileName) {
    return <Text>--</Text>;
  }

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      className={container}
      data-testid="profile-name-cell-container"
    >
      <Text>{profileName}</Text>

      {enableCiC && (
        <Flex
          alignItems="center"
          className={classNames(iconContainer, loading && iconContainerVisible)}
        >
          {loading ? (
            <LoadingIndicator size="small" />
          ) : (
            <HelpIndicator
              id={`create-change-in-circumstance-tooltip-${data?.profileGuid}`}
              placement="top"
              content={
                activeCicCase
                  ? "View change in circumstance case"
                  : "Create change in circumstance case"
              }
            >
              <Icon
                type={activeCicCase ? "external-link" : "plus"}
                variant="outline"
                color={Colors.Primary600}
                onClick={handleIconClick}
                className={icon}
                data-testid="create-change-in-circumstance-icon"
              />
            </HelpIndicator>
          )}
        </Flex>
      )}
    </Flex>
  );
};
